import api from './api';
import { Training, TrainingProvider, TrainingRegistration, TrainingInvoice, TrainingReview, TrainingReviewAverages, AcademyHoofdgroepInfo, AcademySubcategorie } from '../types';

// ─── Public / Professional ────────────────────────────────────────────────────

export interface OpleidingenFilter {
  soort?: string;
  niveau?: string;
  zoek?: string;
  pagina?: number;
  per_pagina?: number;
}

export const getOpleidingen = async (filter: OpleidingenFilter = {}) => {
  const params = new URLSearchParams();
  if (filter.soort) params.set('soort', filter.soort);
  if (filter.niveau) params.set('niveau', filter.niveau);
  if (filter.zoek) params.set('zoek', filter.zoek);
  if (filter.pagina) params.set('pagina', String(filter.pagina));
  if (filter.per_pagina) params.set('per_pagina', String(filter.per_pagina));
  const response = await api.get(`/opleidingen?${params}`);
  return response.data;
};

export const getOpleiding = async (id: number): Promise<Training> => {
  const response = await api.get(`/opleidingen/${id}`);
  return response.data.data;
};

export const aanmeldenOpleiding = async (id: number, opmerkingen?: string): Promise<TrainingRegistration> => {
  const response = await api.post(`/opleidingen/${id}/aanmelden`, { opmerkingen });
  return response.data.data;
};

export const getMijnAanmeldingen = async (): Promise<TrainingRegistration[]> => {
  const response = await api.get('/opleidingen/mijn-aanmeldingen');
  return response.data.data;
};

export const getMijnFacturen = async (): Promise<TrainingInvoice[]> => {
  const response = await api.get('/opleidingen/mijn-facturen');
  return response.data.data;
};

// Registratie: zie opleiderOnboardingApi.ts (onboarding-wizard, migratie 149).

// ─── Provider Profile ─────────────────────────────────────────────────────────

// Stripe Connect (uitbetaling Academy-boekingen naar de opleider)
export type OpleiderStripeStatus = {
  onboarding_status: 'not_started' | 'pending' | 'verified' | 'rejected';
  capability_status_code: string | null;
  onboarding_url: string | null;
};
export const getOpleiderStripeStatus = async (): Promise<OpleiderStripeStatus> => {
  const response = await api.get('/opleider/stripe/status');
  return response.data.data;
};
export const createOpleiderStripeOnboardingLink = async (): Promise<{ onboarding_url: string }> => {
  const response = await api.post('/opleider/stripe/onboarding-link');
  return response.data.data;
};
export const createOpleiderStripeDashboardLink = async (): Promise<{ dashboard_url: string }> => {
  const response = await api.post('/opleider/stripe/dashboard-link');
  return response.data.data;
};

export const getProviderMe = async (): Promise<TrainingProvider> => {
  const response = await api.get('/opleidingen/providers/me');
  return response.data.data;
};

export const updateProviderMe = async (data: Partial<TrainingProvider>): Promise<TrainingProvider> => {
  const response = await api.put('/opleidingen/providers/me', data);
  return response.data.data;
};

export const uploadProviderLogo = async (file: File): Promise<{ logo_url: string }> => {
  const formData = new FormData();
  formData.append('logo', file);
  const response = await api.post('/opleidingen/providers/me/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

export const signProviderContract = async (signatureData: string): Promise<TrainingProvider> => {
  const response = await api.post('/opleidingen/providers/me/contract/sign', {
    signature_data: signatureData,
  });
  return response.data.data;
};

// ─── Belagom Academy catalogus ────────────────────────────────────────────────

export const getAcademySubcategorieen = async (): Promise<{ hoofdgroepen: AcademyHoofdgroepInfo[]; subcategorieen: AcademySubcategorie[] }> => {
  const response = await api.get('/opleidingen/academy/subcategorieen');
  return response.data.data;
};

// ─── Academy-startmomenten (sessies met lesdata + capaciteit) ─────────────────

export interface OpleiderAcademySessie {
  id: number;
  naam: string | null;
  max_deelnemers: number;
  geboekt: number;
  vrij: number;
  status: 'AVAILABLE' | 'LOCKED_FULL' | 'EXPIRED';
  start_at: string | null;
  lessen: Array<{ volgnummer: number; start_at: string; eind_at: string; locatie: string | null }>;
}

export const getAcademySessies = async (trainingId: number): Promise<OpleiderAcademySessie[]> => {
  const response = await api.get(`/opleidingen/providers/me/trainings/${trainingId}/academy-sessies`);
  return response.data.data;
};

export const maakAcademySessie = async (
  trainingId: number,
  data: { naam: string | null; max_deelnemers: number; lessen: Array<{ start_at: string; eind_at: string; locatie: string | null }> }
): Promise<{ id: number }> => {
  const response = await api.post(`/opleidingen/providers/me/trainings/${trainingId}/academy-sessies`, data);
  return response.data.data;
};

export const wijzigAcademySessieCapaciteit = async (trainingId: number, sessieId: number, maxDeelnemers: number): Promise<void> => {
  await api.patch(`/opleidingen/providers/me/trainings/${trainingId}/academy-sessies/${sessieId}`, { max_deelnemers: maxDeelnemers });
};

export const verwijderAcademySessie = async (trainingId: number, sessieId: number): Promise<void> => {
  await api.delete(`/opleidingen/providers/me/trainings/${trainingId}/academy-sessies/${sessieId}`);
};

// ─── Provider Trainings ───────────────────────────────────────────────────────

export const getProviderTrainings = async (): Promise<Training[]> => {
  const response = await api.get('/opleidingen/providers/me/trainings');
  return response.data.data;
};

export const createTraining = async (data: Partial<Training>): Promise<Training> => {
  const response = await api.post('/opleidingen/providers/me/trainings', data);
  return response.data.data;
};

export const updateTraining = async (id: number, data: Partial<Training>): Promise<Training> => {
  const response = await api.put(`/opleidingen/providers/me/trainings/${id}`, data);
  return response.data.data;
};

export const deleteTraining = async (id: number): Promise<void> => {
  await api.delete(`/opleidingen/providers/me/trainings/${id}`);
};

export const togglePublishTraining = async (id: number): Promise<Training> => {
  const response = await api.post(`/opleidingen/providers/me/trainings/${id}/publish`);
  return response.data.data;
};

export const uploadOmslagfoto = async (id: number, file: File): Promise<{ omslagfoto_url: string }> => {
  const formData = new FormData();
  formData.append('omslagfoto', file);
  const response = await api.post(`/opleidingen/providers/me/trainings/${id}/omslagfoto`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

export const uploadBrochure = async (id: number, file: File): Promise<{ brochure_url: string }> => {
  const formData = new FormData();
  formData.append('brochure', file);
  const response = await api.post(`/opleidingen/providers/me/trainings/${id}/brochure`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

// ─── Provider Registrations & Invoices ───────────────────────────────────────

export const getProviderRegistrations = async (trainingId?: number): Promise<TrainingRegistration[]> => {
  const params = trainingId ? `?training_id=${trainingId}` : '';
  const response = await api.get(`/opleidingen/providers/me/registrations${params}`);
  return response.data.data;
};

export const getProviderInvoices = async (): Promise<TrainingInvoice[]> => {
  const response = await api.get('/opleidingen/providers/me/invoices');
  return response.data.data;
};

export interface CreateInvoiceData {
  registration_id: number;
  bedrag_excl_btw: number;
  btw_percentage?: number;
  notities?: string;
}

export const createTrainingInvoice = async (data: CreateInvoiceData): Promise<TrainingInvoice> => {
  const response = await api.post('/opleidingen/providers/me/invoices', data);
  return response.data.data;
};

// ─── Employer / Opdrachtgever ─────────────────────────────────────────────────

export interface EmployerOpleidingenFilter {
  categorie?: string;
  soort?: string;
  zoek?: string;
  pagina?: number;
  per_pagina?: number;
}

export const getEmployerOpleidingen = async (filter: EmployerOpleidingenFilter = {}) => {
  const params = new URLSearchParams();
  if (filter.categorie) params.set('categorie', filter.categorie);
  if (filter.soort) params.set('soort', filter.soort);
  if (filter.zoek) params.set('zoek', filter.zoek);
  if (filter.pagina) params.set('pagina', String(filter.pagina));
  if (filter.per_pagina) params.set('per_pagina', String(filter.per_pagina));
  const response = await api.get(`/opleidingen/voor-ondernemingen?${params}`);
  return response.data;
};

// ─── Admin ────────────────────────────────────────────────────────────────────

export const adminGetProviders = async (): Promise<TrainingProvider[]> => {
  const response = await api.get('/opleidingen/admin/providers');
  return response.data.data;
};

export const adminToggleProvider = async (id: number): Promise<TrainingProvider> => {
  const response = await api.put(`/opleidingen/admin/providers/${id}/activate`);
  return response.data.data;
};

export const adminGetTrainings = async (): Promise<Training[]> => {
  const response = await api.get('/opleidingen/admin/trainings');
  return response.data.data;
};

export const adminGetInvoices = async (): Promise<TrainingInvoice[]> => {
  const response = await api.get('/opleidingen/admin/invoices');
  return response.data.data;
};

export const adminMarkInvoiceBetaald = async (id: number): Promise<TrainingInvoice> => {
  const response = await api.put(`/opleidingen/admin/invoices/${id}/betaald`);
  return response.data.data;
};

export const adminDoorstorten = async (id: number): Promise<TrainingInvoice> => {
  const response = await api.put(`/opleidingen/admin/invoices/${id}/doorstorten`);
  return response.data.data;
};

// ─── Reviews ──────────────────────────────────────────────────────────────────

/**
 * 3-Assen Review Model: expertise = As 1 (inhoud), toepasbaarheid = As 2 (toepasbaarheid
 * of welzijn bij passie-cursussen), structuur = As 3. Medewerkers beantwoorden alleen
 * de 3 assen + aanbeveling.
 */
export interface SubmitReviewData {
  relevantie?: number;
  toepasbaarheid: number;
  expertise: number;
  structuur: number;
  prijs_kwaliteit?: number;
  algemene_ervaring?: number;
  aanbeveling: number;
  open_comment?: string;
}

export interface ReviewVragen {
  markt: 'medewerker' | 'professional' | 'employee' | null;
  as2_variant: 'toepasbaarheid' | 'welzijn';
}

export const getReviewVragen = async (trainingId: number): Promise<ReviewVragen> => {
  const response = await api.get(`/opleidingen/${trainingId}/review-vragen`);
  return response.data.data;
};

export const submitTrainingReview = async (trainingId: number, data: SubmitReviewData): Promise<TrainingReview> => {
  const response = await api.post(`/opleidingen/${trainingId}/review`, data);
  return response.data.data;
};

export const getTrainingReviews = async (trainingId: number): Promise<{
  reviews: TrainingReview[];
  averages: TrainingReviewAverages;
  count: number;
}> => {
  const response = await api.get(`/opleidingen/${trainingId}/reviews`);
  return response.data.data;
};

export const getMyTrainingReview = async (trainingId: number): Promise<TrainingReview | null> => {
  const response = await api.get(`/opleidingen/${trainingId}/my-review`);
  return response.data.data;
};

// ─── Opleiders-dashboard (Menu 1): Actiecentrum & Meldingenfeed ───────────────
// Backend: src/routes/opleiderDashboard.ts (/api/opleider/dashboard)

export type OpleiderSignalCategory = 'commercieel' | 'operationeel' | 'financieel' | 'kwaliteit';
export type OpleiderSignalSeverity = 'urgent' | 'actie' | 'info';

export interface OpleiderSignal {
  id: string;
  key: string;
  eventId: string | null;
  source: 'afgeleid' | 'notificatie';
  category: OpleiderSignalCategory;
  severity: OpleiderSignalSeverity;
  params: Record<string, string | number | null>;
  title?: string;
  content?: string;
  deeplink: string;
  occurredAt: string;
  isRead: boolean;
}

export interface OpleiderSignalenResponse {
  signalen: OpleiderSignal[];
  tellers: Partial<Record<OpleiderSignalCategory, { totaal: number; ongelezen: number }>>;
  ongelezen: number;
  eventIds: Record<string, string>;
}

export const getOpleiderSignalen = async (): Promise<OpleiderSignalenResponse> => {
  const response = await api.get('/opleider/dashboard/signalen');
  return response.data.data;
};

export const markeerOpleiderSignaalGelezen = async (signalId: string): Promise<void> => {
  await api.post('/opleider/dashboard/signalen/gelezen', { signal_id: signalId });
};

// ─── Admin: cursus-audit (compliance-keuring) ─────────────────────────────────

export type AuditStatus = 'IN_REVIEW' | 'APPROVED' | 'REJECTED';

export interface AuditTraining {
  id: number;
  titel: string;
  soort_opleiding: string | null;
  academy_categorie: string | null;
  b2b_rij: string | null;
  pro_hoofdmap: string | null;
  is_published: boolean;
  created_at: string;
  audit_status: AuditStatus;
  audit_toelichting: string | null;
  audit_aanbevolen_rij: string | null;
  audit_beslist_at: string | null;
  organisatie_naam: string | null;
  kvk_handelsnaam: string | null;
}

export const getAuditTrainings = async (status: AuditStatus): Promise<AuditTraining[]> => {
  const response = await api.get('/opleidingen/admin/audit', { params: { status } });
  return response.data.data;
};

export const zetAuditUitslag = async (
  trainingId: number,
  body: { status: 'APPROVED' | 'REJECTED'; toelichting?: string | null; aanbevolen_rij?: string | null }
): Promise<void> => {
  await api.post(`/opleidingen/admin/trainings/${trainingId}/audit`, body);
};
